import React from 'react';
import { Modal, Row, Col, Input, Select, Button, Space } from 'antd';
import { Controller } from 'react-hook-form';
import InspectionBtn from '@/components/InspectionBtn';

const { Option } = Select;

const ShipForm = ({
    isVisible,
    isEditMode,
    onCancel,
    onSubmit,
    control,
    errors,
    handleSubmit,
    vesselTypes,
    engineTypes
}) => {
    const modalTitle = isEditMode ? "Edit Ship" : "Add New Ship";
    const submitButtonText = isEditMode ? "Update Ship" : "Add Ship";
    const submitButtonClass = isEditMode 
        ? "bg-green-600 hover:bg-green-700 border-green-600 hover:border-green-700"
        : "bg-green-600 hover:bg-green-700 border-blue-600 hover:border-blue-700";

    return (
        <Modal
            title={modalTitle}
            open={isVisible}
            onCancel={onCancel}
            footer={null}
            width={800}
            destroyOnClose
        >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {/* Hidden IMO Number field for editing */}
                {isEditMode && (
                    <Controller
                        name="imo_number"
                        control={control}
                        render={({ field }) => <input type="hidden" {...field} />}
                    />
                )}

                <Row gutter={16}>
                      <Col span={12}>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                IMO Number *
                            </label>
                            <Controller
                                name="imo_number"
                                control={control}
                                rules={{ required: 'IMO NUMBER is required' }}
                                render={({ field }) => (
                                    <Input 
                                        {...field} 
                                        placeholder="Enter ship number"
                                        status={errors.name ? 'error' : ''}
                                    />
                                )}
                            />
                            {errors.name && (
                                <div className="text-red-500 text-xs mt-1">
                                    {errors.name.message}
                                </div>
                            )}
                        </div>
                    </Col>

                    <Col span={12}>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Ship Name *
                            </label>
                            <Controller
                                name="name"
                                control={control}
                                rules={{ required: 'Ship name is required' }}
                                render={({ field }) => (
                                    <Input 
                                        {...field} 
                                        placeholder="Enter ship name"
                                        status={errors.name ? 'error' : ''}
                                    />
                                )}
                            />
                            {errors.name && (
                                <div className="text-red-500 text-xs mt-1">
                                    {errors.name.message}
                                </div>
                            )}
                        </div>
                    </Col>
                    <Col span={12}>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Vessel Type *
                            </label>
                            <Controller
                                name="vessel_type"
                                control={control}
                                rules={{ required: 'Vessel type is required' }}
                                render={({ field }) => (
                                    <Select 
                                        {...field} 
                                        placeholder="Select vessel type" 
                                        className="w-full"
                                        status={errors.vessel_type ? 'error' : ''}
                                    >
                                        {vesselTypes.map(type => (
                                            <Option key={type} value={type}>{type}</Option>
                                        ))}
                                    </Select>
                                )}
                            />
                            {errors.vessel_type && (
                                <div className="text-red-500 text-xs mt-1">
                                    {errors.vessel_type.message}
                                </div>
                            )}
                        </div>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Deadweight (DWT)
                            </label>
                            <Controller
                                name="deadweight"
                                control={control}
                                render={({ field }) => (
                                    <Input 
                                        {...field} 
                                        placeholder="e.g., 50,000"
                                    />
                                )}
                            />
                        </div>
                    </Col>
                    <Col span={12}>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Gross Tonnage
                            </label>
                            <Controller
                                name="gross_tonnage"
                                control={control}
                                render={({ field }) => (
                                    <Input 
                                        {...field} 
                                        placeholder="e.g., 30,000"
                                    />
                                )}
                            />
                        </div>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Flag
                            </label>
                            <Controller
                                name="flag"
                                control={control}
                                render={({ field }) => (
                                    <Input 
                                        {...field} 
                                        placeholder="Country of registration"
                                    />
                                )}
                            />
                        </div>
                    </Col>
                    <Col span={12}>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Year of Build
                            </label>
                            <Controller
                                name="year_of_build"
                                control={control}
                                render={({ field }) => (
                                    <Input 
                                        {...field} 
                                        placeholder="e.g., 2020"
                                        type="number"
                                    />
                                )}
                            />
                        </div>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Engine Type
                            </label>
                            <Controller
                                name="engine_type"
                                control={control}
                                render={({ field }) => (
                                    <Select 
                                        {...field} 
                                        placeholder="Select engine type" 
                                        className="w-full"
                                        allowClear
                                    >
                                        {engineTypes.map(type => (
                                            <Option key={type} value={type}>{type}</Option>
                                        ))}
                                    </Select>
                                )}
                            />
                        </div>
                    </Col>
                    <Col span={12}>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Engine Model
                            </label>
                            <Controller
                                name="engine_model"
                                control={control}
                                render={({ field }) => (
                                    <Input 
                                        {...field} 
                                        placeholder="Engine model"
                                    />
                                )}
                            />
                        </div>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Engine Power
                            </label>
                            <Controller
                                name="engine_power"
                                control={control}
                                render={({ field }) => (
                                    <Input 
                                        {...field} 
                                        placeholder="e.g., 15,000 kW"
                                    />
                                )}
                            />
                        </div>
                    </Col>
                </Row>

                <div className="text-right pt-4">
                    <Space>
                        <InspectionBtn variant="secondary" onClick={onCancel}>
                            Cancel
                        </InspectionBtn>
                        <InspectionBtn 
                            type="primary" 
                            htmlType="submit"
                            className={submitButtonClass}
                        >
                            {submitButtonText}
                        </InspectionBtn>
                    </Space>
                </div>
            </form>
        </Modal>
    );
};

export default ShipForm;